
import { Component, Prop, Vue } from 'vue-property-decorator';
import Themeable from 'vuetify/lib/mixins/themeable';
import { CommentRepositoryInterface } from '@/services/QC-V2/comment/interfaces';
import { Roles } from '@/services/Roles';
import {
  CommentRate,
  ReplyCommentInterface,
  ReplyInterface,
  TicketInterface,
} from '@/services/QC-V2/ticket/interfaces';
import { CommentCreateInterface, ProfileMapInterface } from '@/services/QC-V2/chat/interfaces';
import DeleteCommentDialog from '@/components/dialogs/qc-v2/comment/DeleteCommentDialog.vue';

@Component({
  mixins: [
    Themeable,
  ],
  components: {
    DeleteCommentDialog,
  },
  inject: [
    'commentRepositoryV2',
  ],
})
export default class TicketReplies extends Vue {
  @Prop() private ticket!: TicketInterface;
  @Prop() private profilesMap!: ProfileMapInterface;
  @Prop({ default: false }) private disabledReplyComments: boolean;

  private loading: boolean = true;

  private commentRepositoryV2!: CommentRepositoryInterface;

  private replyIdForComment: number = 0;
  private commentText: string = '';
  private commentRates: typeof CommentRate = CommentRate;
  private commentRate: CommentRate = CommentRate.NOT_RATED;

  private commentIdForEdit: number = 0;
  private commentEditText: string = '';
  private userRoles: object = Roles;

  private deleteCommentDialogVisible: boolean = false;
  private deleteCommentDialogEvent!: string;
  private deleteCommentDialogData!: { ticketId?: number, commentId?: number, index?: number, replyId?: number };

  private get windowHeight(): number {
    return window.screen.height - 350;
  }

  private bindReplyClass(reply: ReplyInterface): string {
    return reply.email.endsWith('@wpx.net') ? 'agent-reply' : 'visitor-reply';
  }

  private bindReplyCommentClass(comment: ReplyCommentInterface): string {
    return `outline-rate-${comment.rate}`;
  }

  private showCommentBox(replyId: number): void {
    if (replyId === this.replyIdForComment) {
      this.replyIdForComment = 0;
      return;
    }

    this.replyIdForComment = replyId;
  }

  private showEditBox(commentId: number, commentText: string): void {
    if (this.commentIdForEdit === commentId) {
      this.commentEditText = '';
      this.commentIdForEdit = 0;
      return;
    }

    this.commentEditText = commentText;
    this.commentIdForEdit = commentId;
  }


  private setCommentRate(rate: CommentRate): void {
    if (rate === this.commentRate) {
      this.commentRate = CommentRate.NOT_RATED;
      return;
    }

    this.commentRate = rate;
  }


  private async createComment(index: number): Promise<void> {
    try {
      const comment = await this.commentRepositoryV2.createReplyComment(this.replyIdForComment, {
        author: this.$store.getters.id,
        text: this.commentText,
        rate: this.commentRate,
        timestamp: new Date().toISOString(),
      });

      this.commentText = '';
      this.replyIdForComment = 0;
      this.commentRate = CommentRate.NOT_RATED;
      this.ticket.replies[index].comments.push(comment);
      this.$successDialog.show('Comment created successfully');
    } catch (e) {
      this.$errorDialog.show(e.response?.data?.message ? e.response.data.message : e.message);
    }
  }

  private async editReplyComment(
    replyId: number,
    replyIndex: number,
    commentId: number,
    commentIndex: number,
  ): Promise<void> {
    try {
      const commentData: CommentCreateInterface = {
        author: this.ticket.replies[replyIndex].comments[commentIndex].author,
        text: this.commentEditText,
        rate: this.commentRate,
        timestamp: new Date().toISOString(),
      };

      await this.commentRepositoryV2.updateReplyComment(replyId, commentId, commentData);
      this.$successDialog.show('Comment Edited Successfully!');
      this.ticket.replies[replyIndex].comments[commentIndex].text = this.commentEditText;

      this.commentIdForEdit = 0;
      this.commentEditText = '';
      this.commentRate = CommentRate.NOT_RATED;
    } catch (e) {
      this.$errorDialog.show(e.response?.data?.message ? e.response.data.message : e.message);
    }
  }

  private showDeleteReplyCommentDialog(replyId: number, commentId: number): void {
    this.deleteCommentDialogData = { replyId, commentId };
    this.deleteCommentDialogEvent = 'reply-comment-deleted';
    this.deleteCommentDialogVisible = true;
  }

  private async deleteComment(data: { replyId: number, commentId: number }): Promise<void> {
    try {
      await this.commentRepositoryV2.deleteReplyComment(data.replyId, data.commentId);
      const messageIndex = this.ticket.replies.findIndex((reply) => reply.id === data.replyId);
      this.$successDialog.show('Comment Deleted Successfully!');
      this.ticket.replies[messageIndex].comments = this.ticket.replies[messageIndex].comments
        .filter((comment) => comment.id !== data.commentId);
      this.$forceUpdate();
      this.deleteCommentDialogVisible = false;
    } catch (e) {
      this.$errorDialog.show(e.response?.data?.message ? e.response.data.message : e.message);
    }
  }
}
